import React, { useState, useEffect, Component } from "react";
import { useNavigate } from "react-router-dom";
import { Loading } from "../components/Loading";
import "../MyCars/NOC.css";
import "./TakeDelivery.css";
import { Button, ImageWithFallback } from "../components/Utilities";
import { apiService, API_ENDPOINTS } from "../../services/apiService";
import { useCarContext } from "../CarContext";
import CardLayout from "../components/CardLayout";
import { UploadKYCModal } from "../components/ModelOverlay/NocModal";
import { ChatModal } from "../components/ModelOverlay/HistoryModal";
import { UploadNameTransferModal } from "../components/ModelOverlay/UploadNameTransferModal";
import { formatOwnership } from "../DealerHome";

// Error Boundary
class ErrorBoundary extends Component {
  state = { hasError: false, error: null };
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="error-boundary">
          <h3>Something went wrong</h3>
          <p>{this.state.error?.message || "An unexpected error occurred."}</p>
        </div>
      );
    }
    return this.props.children;
  }
}

export default function NOC() {
  const navigate = useNavigate();
  const { setNocCount } = useCarContext();
  const [selectedTab] = useState("NOC");

  // KYC
  const [isUploadKYCModalOpen, setIsUploadKYCModalOpen] = useState(false);
  const [isViewKYCModalOpen, setIsViewKYCModalOpen] = useState(false);
  const [selectedKycHistory, setSelectedKycHistory] = useState(null);

  // RC
  const [isUploadNameTransferModalOpen, setIsUploadNameTransferModalOpen] =
    useState(false);
  const [isViewRCModalOpen, setIsViewRCModalOpen] = useState(false);
  const [selectedRCData, setSelectedRCData] = useState(null);

  const [isChatModalOpen, setIsChatModalOpen] = useState(false);
  const [selectedCarId, setSelectedCarId] = useState(null);
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /* -------------------------------------------------- FETCH -------------------------------------------------- */
  useEffect(() => {
    const fetchNOCCars = async () => {
      try {
        setLoading(true);
        const response = await apiService.getDealerNOCCars();
        const allCars = response.data?.cars || [];

        const validCars = allCars.map((car, index) => {
          const originalCarId =
            car.id || `car-${Math.random().toString(36).substr(2, 9)}`;
          return {
            id: `${originalCarId}-NOC-${index}`,
            originalCarId,
            brand: car.make || "Unknown",
            model: car.model || "Unknown",
            rto: car.regno || "N/A",
            fuel: car.fueltype || "Unknown",
            transmission: car.geartype || "Manual",
            year: Number(car.year) || 0,
            km: Number(car.kmsrun) || 0,
            evaluationImages: car.evaluationImages || [],
            car_image_urls: car.car_image_urls || [],
            location: car.yard || "Unknown Location",
            rc:
              car.rcstatus?.toUpperCase() === "YES" ||
              car.rcstatus?.toUpperCase() === "Y" ||
              car.rcstatus?.toUpperCase() === "AVAILABLE",
            owner: formatOwnership(car.ownership),
            auctionId: car.auctionId || "Unknown",
            state: car.state || "DELIVERY_APPROVED",
            kycData: car.kycData,
            kycHistory: car.kycHistory || [],
            nocData: car.nocData,
            rcData: car.rcData, // <-- keep rcData
            rcHistory: car.rcHistory || [],
            startDate: car["delivery_confirmed_at"],
            endDate: car["next_deadline"],
          };
        });

        setCars(validCars);
        setNocCount(validCars.length);
        setError(null);
      } catch (err) {
        console.error("Error fetching dealer NOC cars:", err);
        setError(
          err.response?.data?.message ||
            "Failed to load cars. Please try again."
        );
        setCars([]);
        setNocCount(0);
      } finally {
        setLoading(false);
      }
    };

    fetchNOCCars();
  }, [setNocCount]);

  /* -------------------------------------------------- KYC HANDLERS -------------------------------------------------- */
  const openUploadKYC = (carId) => {
    setSelectedCarId(carId);
    setIsUploadKYCModalOpen(true);
  };
  const closeUploadKYC = () => {
    setIsUploadKYCModalOpen(false);
    setSelectedCarId(null);
    window.location.reload();
  };
  const openViewKYC = (car) => {
    const latest = car.kycHistory?.[0] || null;
    setSelectedKycHistory(latest);
    setSelectedCarId(car.originalCarId);
    setIsViewKYCModalOpen(true);
  };
  const closeViewKYC = () => {
    setIsViewKYCModalOpen(false);
    setSelectedKycHistory(null);
    setSelectedCarId(null);
    window.location.reload();
  };
  const handleSubmitKYC = () => window.location.reload();

  /* -------------------------------------------------- NOC ACKNOWLEDGE -------------------------------------------------- */
  const handleAcknowledgeNOC = async (carId) => {
    try {
      await apiService.acknowledgeNOCReceipt({ carId });
    } catch (err) {
      console.error("Error acknowledging NOC:", err);
      alert(
        err.response?.data?.message ||
          "Failed to acknowledge NOC receipt. Please try again."
      );
    } finally {
      window.location.reload();
    }
  };

  /* -------------------------------------------------- RC HANDLERS -------------------------------------------------- */
  const openUploadNameTransfer = (carId) => {
    setSelectedCarId(carId);
    setIsUploadNameTransferModalOpen(true);
  };
  const closeUploadNameTransfer = () => {
    setIsUploadNameTransferModalOpen(false);
    setSelectedCarId(null);
    window.location.reload();
  };
  const handleSubmitNameTransfer = () => window.location.reload();

  const openViewRC = (car) => {
    setSelectedRCData(car.rcData);
    setSelectedCarId(car.originalCarId);
    setIsViewRCModalOpen(true);
  };
  const closeViewRC = () => {
    setIsViewRCModalOpen(false);
    setSelectedRCData(null);
    setSelectedCarId(null);
    window.location.reload();
  };

  /* -------------------------------------------------- HISTORY -------------------------------------------------- */
  const handleViewHistory = (carId) => navigate(`/car-details/${carId}`);

  /* -------------------------------------------------- ACTION HELPERS -------------------------------------------------- */
  const getCarActionHandler = (car) => {
    const state = car.state;
    switch (state) {
      case "DELIVERY_APPROVED":
        return {
          handler: () => openUploadKYC(car.originalCarId),
          label: "Upload KYC Details",
          secondaryHandler: () => handleViewHistory(car.originalCarId),
        };

      case "KYC_SUBMITTED":
      case "KYC_NEED_FURTHER_INFO":
        return {
          handler: () => openViewKYC(car),
          label: "View KYC Details",
          secondaryHandler: () => handleViewHistory(car.originalCarId),
          secondaryLabel: "View History",
        };
      case "KYC_ACCEPTED":
        return {
          handler: () => handleViewHistory(car.originalCarId),
          label: "KYC Accepted",
          disableButton: true,
          secondaryHandler: () => handleViewHistory(car.originalCarId),
          secondaryLabel: "View History",
        };

      case "NOC_SENT":
        return {
          handler: () => handleAcknowledgeNOC(car.originalCarId),
          label: "Acknowledge NOC Receipt",
          secondaryHandler: () => handleViewHistory(car.originalCarId),
          secondaryLabel: "View History",
        };
      case "NOC_RECEIVED":
        return {
          handler: () => openUploadNameTransfer(car.originalCarId),
          label: "Upload New RC Book",
          secondaryHandler: () => handleViewHistory(car.originalCarId),
          secondaryLabel: "View History",
        };
      case "NEW_RC_UPLOADED":
      case "RC_NEED_MORE_INFO":
        return {
          handler: () => openViewRC(car),
          label: "View RC",
          secondaryHandler: () => handleViewHistory(car.originalCarId),
          secondaryLabel: "View History",
        };

      case "RC_ACCEPTED":
        return {
          handler: () => handleViewHistory(car.originalCarId),
          label: "RC Accepted",
          secondaryHandler: () => handleViewHistory(car.originalCarId),
          secondaryLabel: "View History",
          disableButton: true,
        };
      default:
        return {
          handler: () => handleViewHistory(car.originalCarId),
          label: "View History",
        };
    }
  };

  const getCarActionButton = (car) => {
    const state = car.state;
    switch (state) {
      case "DELIVERY_APPROVED":
        return (
          <Button
            className="btn-full"
            variant="primary"
            onClick={() => openUploadKYC(car.originalCarId)}
          >
            Upload KYC Details
          </Button>
        );

      case "KYC_SUBMITTED":
        return (
          <div className="status-message">
            <p>KYC submitted and pending BDM approval</p>
            <Button
              className="btn-full"
              variant="primary"
              onClick={() => openViewKYC(car)}
            >
              View KYC Details
            </Button>
            <Button
              className="btn-full"
              variant="secondary"
              onClick={() => handleViewHistory(car.originalCarId)}
            >
              View History
            </Button>
          </div>
        );

      case "KYC_NEED_FURTHER_INFO":
        return (
          <div className="status-message">
            <p>KYC needs further information. Please resubmit.</p>
            <Button
              className="btn-full"
              variant="primary"
              onClick={() => openUploadKYC(car.originalCarId)}
            >
              Resubmit KYC
            </Button>
            <Button
              className="btn-full"
              variant="secondary"
              onClick={() => openViewKYC(car)}
            >
              View KYC Details
            </Button>
            <Button
              className="btn-full"
              variant="secondary"
              onClick={() => handleViewHistory(car.originalCarId)}
            >
              View History
            </Button>
          </div>
        );

      case "KYC_ACCEPTED":
        return (
          <div className="status-message">
            <p>KYC approved. Waiting for NOC documents from BDM.</p>
            <Button
              className="btn-full"
              variant="primary"
              onClick={() => openViewKYC(car)}
            >
              View KYC Details
            </Button>
            <Button
              className="btn-full"
              variant="secondary"
              onClick={() => handleViewHistory(car.originalCarId)}
            >
              View History
            </Button>
          </div>
        );

      case "NOC_SENT":
        return (
          <div className="status-message">
            <p>NOC documents sent. Please acknowledge receipt.</p>
            <Button
              className="btn-full"
              variant="primary"
              onClick={() => handleAcknowledgeNOC(car.originalCarId)}
            >
              Acknowledge NOC Receipt
            </Button>
            <Button
              className="btn-full"
              variant="secondary"
              onClick={() => handleViewHistory(car.originalCarId)}
            >
              View History
            </Button>
          </div>
        );

      case "NOC_RECEIVED":
        return (
          <div className="status-message">
            <p>NOC received. Please upload new RC book.</p>
            <Button
              className="btn-full"
              variant="primary"
              onClick={() => openUploadNameTransfer(car.originalCarId)}
            >
              Upload New RC Book
            </Button>
            <Button
              className="btn-full"
              variant="secondary"
              onClick={() => handleViewHistory(car.originalCarId)}
            >
              View History
            </Button>
          </div>
        );

      case "NEW_RC_UPLOADED":
        return (
          <div className="status-message">
            <p>New RC uploaded and pending BDM approval.</p>
            <Button
              className="btn-full"
              variant="primary"
              onClick={() => openViewRC(car)}
            >
              View RC
            </Button>
            <Button
              className="btn-full"
              variant="secondary"
              onClick={() => handleViewHistory(car.originalCarId)}
            >
              View History
            </Button>
          </div>
        );

      case "RC_NEED_MORE_INFO":
        return (
          <div className="status-message">
            <p>RC needs more information. Please resubmit.</p>
            <Button
              className="btn-full"
              variant="primary"
              onClick={() => openUploadNameTransfer(car.originalCarId)}
            >
              Resubmit RC
            </Button>
            <Button
              className="btn-full"
              variant="secondary"
              onClick={() => openViewRC(car)}
            >
              View RC
            </Button>
            <Button
              className="btn-full"
              variant="secondary"
              onClick={() => handleViewHistory(car.originalCarId)}
            >
              View History
            </Button>
          </div>
        );

      case "RC_ACCEPTED":
        return (
          <div className="status-message success">
            <p>RC accepted! NOC process completed.</p>
            <Button
              className="btn-full"
              variant="primary"
              onClick={() => handleViewHistory(car.originalCarId)}
            >
              RC Accepted
            </Button>
            <Button
              className="btn-full"
              variant="secondary"
              onClick={() => openViewRC(car)}
            >
              View RC
            </Button>
          </div>
        );
      default:
        return (
          <div className="status-message">
            <p>Status: {state}</p>
            <Button
              className="btn-full"
              variant="secondary"
              onClick={() => handleViewHistory(car.originalCarId)}
            >
              View History
            </Button>
          </div>
        );
    }
  };

  /* -------------------------------------------------- RENDER -------------------------------------------------- */
  return (
    <ErrorBoundary>
      <div className="noc">
        <div className="content-wrapper">
          <div className="main-content">
            <div className="car-grid-container">
              {loading && <Loading />}
              {error && (
                <div className="no-results">
                  <h3>Error</h3>
                  <p>{error}</p>
                  <Button
                    className="see-more"
                    variant="primary"
                    onClick={() => navigate("/DealerHome")}
                  >
                    Back to Home
                  </Button>
                </div>
              )}
              {!loading && !error && cars.length === 0 && (
                <div className="no-results">
                  <h3>No cars found</h3>
                  <p>No car at this stage for end customer ownership update</p>
                  <Button
                    className="see-more"
                    variant="primary"
                    onClick={() => navigate("/DealerHome")}
                  >
                    Back to Home
                  </Button>
                </div>
              )}
              {!loading && !error && cars.length > 0 && (
                <div className="car-grid">
                  {cars.map((car) => {
                    const {
                      handler,
                      label,
                      secondaryHandler,
                      secondaryLabel,
                      disableButton = false,
                    } = getCarActionHandler(car);
                    return (
                      <CardLayout
                        key={car.id}
                        car={car}
                        btn1={handler}
                        btn1_label={label}
                        btn2={secondaryHandler}
                        btn2_label={secondaryLabel}
                        showBidInfo={false}
                        isDisabled={disableButton}
                      />
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* KYC */}
        <UploadKYCModal
          isOpen={isUploadKYCModalOpen}
          onClose={closeUploadKYC}
          carId={selectedCarId}
          onSubmitKYC={handleSubmitKYC}
        />
        <UploadKYCModal
          isOpen={isViewKYCModalOpen}
          onClose={closeViewKYC}
          carId={selectedCarId}
          kycData={selectedKycHistory}
          readOnly={true}
        />

        {/* RC */}
        <UploadNameTransferModal
          isOpen={isUploadNameTransferModalOpen}
          onClose={closeUploadNameTransfer}
          carId={selectedCarId}
          onSubmitNameTransfer={handleSubmitNameTransfer}
        />
        <UploadNameTransferModal
          isOpen={isViewRCModalOpen}
          onClose={closeViewRC}
          carId={selectedCarId}
          rcData={selectedRCData}
          readOnly={true}
        />

        {/* History */}
        <ChatModal
          isOpen={isChatModalOpen}
          onClose={() => setIsChatModalOpen(false)}
          carId={selectedCarId}
        />
      </div>
    </ErrorBoundary>
  );
}
